import { useMemo, useState } from "react"
import { Card, CardContent, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { Bound } from "lib/@components/binding/Bound"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import Iconify from "minimals-template/components/Iconify"
import {
    BREAKDOWN_VIEW_ASSET,
    BREAKDOWN_VIEW_SCHEDULE,
    BREAKDOWN_VIEW_TRADE,
    buildCostBreakdownRows,
    filterCostBreakdownRows,
    getBreakdownTotals,
    getCostBreakdownViewConfig,
    hasBillingAdjustment,
} from "./cost-breakdown-view-utils"
import { CostBreakdownTable } from "./cost-breakdown-table"
import { ServerCostBreakdown } from "./server-cost-breakdown"

export function CostResourceTable({ costResult, currencySymbol, scenarioId, variantId }) {
    const [view, setView] = useState(BREAKDOWN_VIEW_TRADE)
    const [search] = useSearchParam("search")
    const searchActive = isSearchActive()
    // Database-backed results carry no inline asset/schedule arrays: those views are
    // served in pages by the detail APIs. The trade view stays inline (bounded).
    const serverMode = !!costResult?.detailsRef?.setKey && !!scenarioId && !!variantId && view !== BREAKDOWN_VIEW_TRADE
    const allRows = useMemo(buildAllRows, [costResult, view, serverMode])
    const rows = useMemo(filterRows, [allRows, view, search])
    const config = useMemo(buildConfig, [view, searchActive])
    const totals = useMemo(buildTotals, [rows])
    const hasAnyBillingAdjustment = useMemo(checkBillingAdjustments, [rows])

    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Resource & Costs</Typography>
                        <ToggleButtonGroup
                            value={view}
                            exclusive
                            onChange={handleViewChange}
                            size="small"
                            data-cy="cost-resource-view-switcher"
                        >
                            <ToggleButton value={BREAKDOWN_VIEW_TRADE}>By Trade</ToggleButton>
                            <ToggleButton value={BREAKDOWN_VIEW_SCHEDULE}>By Schedule</ToggleButton>
                            <ToggleButton value={BREAKDOWN_VIEW_ASSET}>By Asset</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                    <If value={isSearchVisible()}>
                        <SearchBoxUsingSearchParam
                            autoFocus={false}
                            placeholder={getSearchPlaceholder()}
                            variant="outlined"
                            py={0}
                            m={0}
                            sx={{ width: 1, maxWidth: 560 }}
                            textFieldProps={{ size: "small", "data-cy": getSearchDataCy() }}
                            startAdornment={<Iconify icon="ic:round-search" height={20} width={20} />}
                        />
                    </If>
                    <Bound hoursPerDay={costResult?.capacity?.hoursPerDay}>
                        <If value={serverMode}>
                            <ServerCostBreakdown
                                key={view}
                                view={view}
                                costResult={costResult}
                                currencySymbol={currencySymbol}
                                config={config}
                                scenarioId={scenarioId}
                                variantId={variantId}
                                search={search}
                            />
                            <Else>
                                <CostBreakdownTable
                                    rows={rows}
                                    config={config}
                                    totals={totals}
                                    costResult={costResult}
                                    currencySymbol={currencySymbol}
                                    showBillingAdjustment={hasAnyBillingAdjustment}
                                    isFiltered={searchActive}
                                />
                            </Else>
                        </If>
                    </Bound>
                </Stack>
            </CardContent>
        </Card>
    )

    function buildAllRows() {
        if (serverMode) return []
        return buildCostBreakdownRows(costResult, view)
    }

    function filterRows() {
        return filterCostBreakdownRows(allRows, view, search)
    }

    function buildConfig() {
        const nextConfig = getCostBreakdownViewConfig(view)
        if (!searchActive) return nextConfig
        if (view === BREAKDOWN_VIEW_ASSET) return { ...nextConfig, emptyMessage: "No assets match your search." }
        return { ...nextConfig, emptyMessage: "No schedules match your search." }
    }

    function buildTotals() {
        return getBreakdownTotals(rows)
    }

    function checkBillingAdjustments() {
        return hasBillingAdjustment(rows)
    }

    function handleViewChange(_event, nextView) {
        if (nextView) setView(nextView)
    }

    function isSearchActive() {
        return isSearchVisible() && !!`${search || ""}`.trim()
    }

    function isSearchVisible() {
        return view === BREAKDOWN_VIEW_SCHEDULE || view === BREAKDOWN_VIEW_ASSET
    }

    function getSearchPlaceholder() {
        if (view === BREAKDOWN_VIEW_ASSET) return "Search by asset type or location..."
        return "Search by schedule code, title or facility..."
    }

    function getSearchDataCy() {
        if (view === BREAKDOWN_VIEW_ASSET) return "cost-resource-asset-search"
        return "cost-resource-schedule-search"
    }
}
